import React, { useEffect, useRef } from "react";
import { compactPrice } from "@/helpers/compactPrice";
import { toneOf } from "@/components/AgentDirectory";
import ShowcaseFitForm from "@/components/ShowcaseFitForm";
import { usePublicShowcaseItem } from "@/hooks/useShowcase";
import "./ShowcaseView.scss";

export const DEFAULT_HEADLINE = "Buyers our agents are working with right now";

export const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "budget", label: "Highest budget" },
];

export const budgetLine = (min, max) => {
  const low = Number(min) > 0 ? compactPrice(min) : null;
  const high = Number(max) > 0 ? compactPrice(max) : null;
  if (low && high) return `${low} to ${high}`;
  if (high) return `Up to ${high}`;
  if (low) return `From ${low}`;
  return "Budget open";
};

const plural = (count, word) => `${count} ${word}${Number(count) === 1 ? "" : "s"}`;

export const factsLine = (item) =>
  [
    Number(item.bedrooms) > 0 ? plural(item.bedrooms, "bed") : null,
    Number(item.bathrooms) > 0 ? plural(item.bathrooms, "bath") : null,
    item.propertyType || null,
  ]
    .filter(Boolean)
    .join(", ");

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

const glue = (text) => {
  const value = String(text || "").trim();
  if (value.includes("\u00a0")) return value;
  const at = value.lastIndexOf(" ");
  return at > 0 ? `${value.slice(0, at)} ${value.slice(at + 1)}` : value;
};

const Avatar = ({ agent }) => (
  <span className={`showcase-view__avatar${agent?.photoUrl ? "" : ` is-tone-${toneOf(agent?.name)}`}`} aria-hidden="true">
    {agent?.photoUrl ? <img src={agent.photoUrl} alt="" loading="lazy" /> : initialsOf(agent?.name)}
  </span>
);

const agentOf = (item) => item.agent || (item.agentName ? { name: item.agentName, slug: item.agentSlug } : null);

const ShowcaseCard = ({ item, onOpen }) => {
  const agent = agentOf(item);
  const facts = factsLine(item);
  const body = (
    <>
      <span className="showcase-view__price">{budgetLine(item.budgetMin, item.budgetMax)}</span>
      <span className="showcase-view__title">{item.title}</span>
      {facts ? <span className="showcase-view__facts">{facts}</span> : null}
      {item.location ? <span className="showcase-view__place">{item.location}</span> : null}
      <span className="showcase-view__card-foot">
        {agent ? (
          <span className="showcase-view__agent">
            <Avatar agent={agent} />
            <span>{agent.name}</span>
          </span>
        ) : (
          <span />
        )}
        <span className="showcase-view__cta">I have a home that&nbsp;fits</span>
      </span>
    </>
  );
  return onOpen ? (
    <button type="button" className="showcase-view__card" onClick={() => onOpen(item.wantAdId)}>
      {body}
    </button>
  ) : (
    <div className="showcase-view__card">{body}</div>
  );
};

const mustHaveText = (entry) => (typeof entry === "string" ? entry : entry?.text || entry?.Text || "");

const ShowcaseDetail = ({ showcaseKey, summary, onBack, brokerageName }) => {
  const query = usePublicShowcaseItem(showcaseKey, summary.wantAdId);
  const item = { ...summary, ...(query.data || {}) };
  const agent = agentOf(item);
  const headingRef = useRef(null);
  const facts = factsLine(item);
  const mustHaves = (item.mustHaves || []).map(mustHaveText).filter(Boolean);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: false });
  }, [summary.wantAdId]);

  return (
    <article className="showcase-view__detail" aria-labelledby="showcase-detail-title">
      <button type="button" className="showcase-view__back" onClick={onBack}>
        All buyers
      </button>
      <p className="showcase-view__price showcase-view__price--large">{budgetLine(item.budgetMin, item.budgetMax)}</p>
      <h2 id="showcase-detail-title" ref={headingRef} tabIndex={-1}>
        {glue(item.title)}
      </h2>
      {facts || item.location ? (
        <p className="showcase-view__facts">{[facts, item.location].filter(Boolean).join(" · ")}</p>
      ) : null}
      {item.description ? <p className="showcase-view__description">{item.description}</p> : null}
      {mustHaves.length ? (
        <div className="showcase-view__musts">
          <h3>Must haves</h3>
          <ul>
            {mustHaves.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {query.isLoading ? <p className="showcase-view__quiet">Loading the details…</p> : null}
      {agent ? (
        <div className="showcase-view__agent showcase-view__agent--detail">
          <Avatar agent={agent} />
          <span>
            {agent.name}
            {brokerageName ? <small>{brokerageName}</small> : null}
          </span>
        </div>
      ) : null}
      {agent?.slug ? (
        <ShowcaseFitForm agent={agent} wantAdId={item.wantAdId} title={item.title} showcaseKey={showcaseKey} />
      ) : null}
    </article>
  );
};

const Skeleton = () => (
  <div className="showcase-view__grid" aria-hidden="true">
    {[0, 1, 2, 3].map((index) => (
      <div key={index} className="showcase-view__card showcase-view__card--skeleton">
        <span />
        <span />
        <span />
      </div>
    ))}
  </div>
);

const ShowcaseView = ({
  brokerage,
  headline,
  items,
  sort,
  onSort,
  loading,
  hasMore,
  loadingMore,
  onMore,
  hostedUrl,
  buyerFormUrl,
  showcaseKey,
  openId,
  onOpenChange,
  preview,
}) => {
  const list = items || [];
  const open = openId ? list.find((item) => item.wantAdId === openId) : null;
  const canOpen = !preview && Boolean(showcaseKey) && Boolean(onOpenChange);

  return (
    <div className={`showcase-view${preview ? " showcase-view--preview" : ""}`}>
      <header className="showcase-view__head">
        {brokerage?.logoUrl ? (
          <img className="showcase-view__logo" src={brokerage.logoUrl} alt={brokerage?.name || ""} />
        ) : brokerage?.name ? (
          <p className="showcase-view__brokerage">{brokerage.name}</p>
        ) : null}
        {open ? null : (
          <>
            <h1>{glue(headline || DEFAULT_HEADLINE)}</h1>
            <p className="showcase-view__lede">
              Own a home that fits one of these buyers? Tell the agent and they will reach&nbsp;out.
            </p>
          </>
        )}
      </header>

      {open ? (
        <ShowcaseDetail
          showcaseKey={showcaseKey}
          summary={open}
          brokerageName={brokerage?.name}
          onBack={() => onOpenChange(null)}
        />
      ) : (
        <>
          {list.length || loading ? (
            <div className="showcase-view__sort" role="radiogroup" aria-label="Sort buyers">
              {SORTS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={sort === option.value}
                  className={sort === option.value ? "is-on" : ""}
                  onClick={() => onSort && sort !== option.value && onSort(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : null}

          {loading && !list.length ? (
            <Skeleton />
          ) : list.length ? (
            <div className="showcase-view__grid">
              {list.map((item) => (
                <ShowcaseCard key={item.wantAdId} item={item} onOpen={canOpen ? onOpenChange : null} />
              ))}
            </div>
          ) : (
            <div className="showcase-view__empty">
              <h2>Tell us what you&apos;re looking&nbsp;for</h2>
              <p>
                Share the home you want and an agent from {brokerage?.name || "our office"} will reach out with homes
                that&nbsp;fit.
              </p>
              {buyerFormUrl ? (
                <a className="showcase-view__button" href={buyerFormUrl} target="_blank" rel="noopener noreferrer">
                  Share what you&nbsp;want
                </a>
              ) : null}
            </div>
          )}

          {hasMore ? (
            <div className="showcase-view__more">
              <button type="button" className="showcase-view__button showcase-view__button--outline" disabled={loadingMore} onClick={onMore}>
                {loadingMore ? "Loading…" : "Show more buyers"}
              </button>
            </div>
          ) : null}
        </>
      )}

      <footer className="showcase-view__foot">
        {hostedUrl ? (
          <a href={hostedUrl} target="_blank" rel="noopener noreferrer">
            Powered by Gitcha
          </a>
        ) : (
          <span>Powered by Gitcha</span>
        )}
        <span>Real-time demand from local&nbsp;agents</span>
      </footer>
    </div>
  );
};

export default ShowcaseView;
